import {
  PersonalStepProjectSchemaType,
  StudentEntrySource,
  StudentTypes,
} from "../schemas"
import {
  Gender,
  LearningPathPipEnum,
  StudentEnrollmentTypeEnum,
  TargetLevelEnum,
} from "../constants"
import { Language } from "./language"
import { BadgeVariant } from "../components"
import { ProjectType } from "./projects"

interface StudentRegistrationAttrs {
  nationality: Language["iso"]
  countryOfBirth: Language["iso"]
  countryOfOrigin: Language["iso"]
  nativeLanguage: Language["iso"]
  otherLanguages: Language[]
  numberOfLanguageHours: number | null
  enrollmentType: StudentEnrollmentTypeEnum
  learningPathPip: LearningPathPipEnum | null
  studentHasExternalHours: boolean
  phoneNumberWhatsapp: string
  houseNumber: string
  houseNumberAddition: string | null
  projectOrganizationId: number | null
  city: string
  street: string
  studentId: number | null
  externalStudentFileNumber: string | null
  gender: Gender
  billingOrganizationId: number | null
  email: string
  secondaryEmail: string
  targetLevelPip: TargetLevelEnum | null
  citizenServiceNumber: string
  requiresExternalHours: boolean
  intakeResultReportFile: string | null
  reportingOrganizationId: number | null
  mustPlanIntake: boolean | null
  comments: string
  entrySource: StudentEntrySource | null
  phoneNumberCall: string
  projectId: number
  projectSchemaType: PersonalStepProjectSchemaType
  projectType: ProjectType
  civicIntegrationStartDate: string
  lastName: string
  birthDate: string
  postalCode: string
  civicIntegrationObligation: boolean | null
  civicIntegrationEndDate: string
  firstName: string
  organizationId: number
  isEntrySourceRequired?: boolean
  studentType: StudentTypes | null
}

type StudentRegistrationStatus = "pending" | "accepted" | "rejected"

type StudentSpoStatus =
  | "never_logged_in"
  | "first_login"
  | "registered"
  | "intake_scheduled"
  | "intake_completed"
  | "intake_missed"
  | "placed"
  | "waitlist"
  | "started"
  | "interrupted"
  | "dropped_out"
  | "finished"

const studentStatusVariantMap: Record<StudentSpoStatus, BadgeVariant> = {
  never_logged_in: "yellow",
  first_login: "yellow",
  registered: "yellow",
  intake_scheduled: "secondaryGreen",
  intake_completed: "green",
  intake_missed: "red",
  placed: "green",
  waitlist: "yellow",
  started: "green",
  interrupted: "yellow",
  dropped_out: "red",
  finished: "secondaryGreen",
}

const studentStatusLabelMap: Record<StudentSpoStatus, string> = {
  never_logged_in: "Nog niet ingelogd",
  first_login: "Ingelogd",
  registered: "Geregistreerd",
  intake_scheduled: "Intake gepland",
  intake_completed: "Intake voltooid",
  intake_missed: "Intake gemist",
  placed: "Geplaatst",
  waitlist: "Wachtlijst",
  started: "Gestart",
  interrupted: "Onderbroken",
  dropped_out: "Uitgevallen",
  finished: "Voltooid",
}

export const STUDENT_STATUS_OPTIONS = (
  Object.entries(studentStatusLabelMap) as [StudentSpoStatus, string][]
).map(([id, name]) => ({ id, name }))

export function studentStatusToBadgeVariant(status?: StudentSpoStatus) {
  return status
    ? studentStatusVariantMap[status]
    : studentStatusVariantMap.finished
}

export function getStudentStatusLabel(status?: StudentSpoStatus): string {
  return status ? studentStatusLabelMap[status] : studentStatusLabelMap.finished
}

interface StudentRegistrationResponse {
  data: {
    attrs: StudentRegistrationAttrs
    inviteStatus: StudentRegistrationStatus
    projectOrganizationId: number
    rejectedMessage: string | null
  }
}

export type {
  StudentRegistrationAttrs,
  StudentRegistrationResponse,
  StudentRegistrationStatus,
  StudentSpoStatus,
}
